import 'element-internals-polyfill';
/* eslint-disable react/no-unknown-property */
import { Component, Prop, h, Element, AttachInternals, Listen, Watch, State, Host, Method } from '@stencil/core';
import '@material/web/checkbox/checkbox';
import type { MdCheckbox } from '@material/web/checkbox/checkbox';
import { awaitDefinedResult, getOverriddenValidationMessage, selectFormSubmitters } from '../../utils/utils';
import { libraryComponentOptions } from '../../settings/settings';

@Component({
  tag: 'omk-checkbox',
  styleUrl: 'omk-checkbox.scss',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class OmkCheckbox {
  @Prop() disabled: boolean = false;
  @Prop({ mutable: true }) checked: boolean = false;
  @Prop({ mutable: true }) indeterminate: boolean = false;
  @Prop() ariaLabel: string | null;
  @Prop() value: string = 'on';
  @Prop({ reflect: true }) required: boolean = false;
  @Prop() name: string = '';
  @Prop() sublabel: string;
  @Prop() labelBefore: boolean = false;
  @Prop() customValidationMessage: string;
  /** @internal Do not set. Forms check this property before submit and when checkValidity() is called. */
  @Prop({ mutable: true }) validity: ValidityState;
  /** @internal Do not set - regard as read-only. The current validation message whether custom or built-in. */
  @Prop({ mutable: true }) validationMessage: string;
  /** Show validation messages immediately instead of waiting for user interaction or form submission. */
  @Prop() alwaysShowValidationMessage: boolean;
  /** Hides the inline validation messages. Used when error messages are to be displayed using other means other than the inline capability. */
  @Prop() hideValidationMessage: boolean;

  @State() hasKeyboardFocus: boolean;

  @Element() el: HTMLElement;
  innerMaterialElement: MdCheckbox;
  innerInputElement: HTMLInputElement;
  @AttachInternals() internals: ElementInternals;
  @State() touched: boolean = false;
  @State() validateAfterNextRender = false;
  @State() labelPresent: boolean = true;

  private initialValue: string;

  @Watch('checked')
  @Watch('indeterminate')
  @Watch('value')
  @Watch('disabled')
  propStateChanged() {
    if (this.checked && !this.indeterminate && !this.disabled) {
      this.internals.setFormValue(this.value);
    } else {
      this.internals.setFormValue(null);
    }
    this.validateAfterNextRender = true;
  }

  @Listen('change')
  @Listen('input')
  @Listen('blur')
  handleUserInput() {
    this.touched = true;
    if (this.innerMaterialElement) {
      this.checked = this.innerMaterialElement.checked;
      this.indeterminate = this.innerMaterialElement.indeterminate;
      this.validate();
    }
  }

  @Listen('focusin')
  handleFocus() {
    this.hasKeyboardFocus = this.innerInputElement.matches(':focus-visible');
  }
  @Listen('focusout')
  handleFocusOut() {
    this.hasKeyboardFocus = false;
  }

  @Listen('click')
  handleClick(event) {
    // handle only programmatic clicks and clicks on external labels
    // internal checkbox and label clicks are handled by the Material element
    // Ignore links
    if (event.target.classList?.contains('omk-inline-link')) return;

    if (
      (event.composedPath()[2] as HTMLElement).classList?.contains('label-text') ||
      (event.composedPath()[0] as HTMLElement).slot === 'component' ||
      event.composedPath()[0] === event.target
    ) {
      this.innerMaterialElement.click();
    }
  }

  @Watch('customValidationMessage')
  @Watch('required')
  validationPropsChanged() {
    this.validateAfterNextRender = true;
  }

  @Watch('alwaysShowValidationMessage')
  showValidationChanged(newValue) {
    if (newValue) {
      this.touched = true;
    }
  }

  /** Reset the checkbox to its default value. */
  @Method()
  async reset() {
    this.value = this.initialValue;
    this.checked = false;
    this.indeterminate = false;
    this.touched = false;
    this.internals.setFormValue(this.checked ? this.value : null);
    if (this.innerMaterialElement) {
      this.innerMaterialElement.checked = false;
      this.innerMaterialElement.indeterminate = false;
    }
    this.validate();
  }

  componentWillLoad() {
    this.initialValue = this.value;
  }

  slotHasChanged() {
    const slotted = this.el.shadowRoot.querySelector('omk-label slot') as HTMLSlotElement;
    this.labelPresent = slotted.assignedElements().length > 0 || slotted.assignedNodes().length > 0;
  }

  render() {
    return (
      <Host class={{ touched: this.touched }}>
        {(this.labelPresent ||
          (!this.hideValidationMessage &&
            (this.touched || this.alwaysShowValidationMessage) &&
            this.validationMessage)) && (
          <omk-label
            for="checkbox"
            id="embedded-label"
            style={{ order: this.labelBefore ? '-1' : '1' }}
            sublabel={this.sublabel}
            validationMessage={this.validationMessage}
            disabled={this.disabled}
            hideValidationMessage={!this.touched || this.hideValidationMessage}
          >
            <slot onSlotchange={() => this.slotHasChanged()}></slot>
          </omk-label>
        )}
        <div
          class={{
            'halo': true,
            'keyboard-focus': this.hasKeyboardFocus,
            'has-value': this.checked || this.indeterminate,
          }}
        >
          <md-checkbox
            id="checkbox"
            ariaLabel={this.ariaLabel}
            disabled={this.disabled}
            indeterminate={this.indeterminate}
            checked={!this.indeterminate && this.checked}
            name={this.name}
            value={this.value}
            required={this.required}
          ></md-checkbox>
        </div>
      </Host>
    );
  }

  /** Run validation for the checkbox with a boolean parameter to set it as touched.
   * If `setTouched` is not provided or set to false, the the touched state will not be changed.
   * Validation messages are not shown until the element is touched.
   * The only built-in validation for checkboxes is "required".
   * Custom validation messages can be set through the customValidationMessage property, and don't require this method to be called afterwards.
   * Returns true if valid or disabled, false if invalid. */
  @Method()
  async validate(setTouched?: boolean) {
    if (setTouched) {
      this.touched = true;
    }
    if (this.disabled) return true;
    if (this.customValidationMessage) {
      this.internals.setValidity({ customError: true }, this.customValidationMessage, this.innerInputElement);
    } else {
      this.internals.setValidity(
        this.innerMaterialElement.validity,
        getOverriddenValidationMessage(
          libraryComponentOptions.componentValidationMessageOverrides['omk-checkbox'],
          this.innerMaterialElement.validity,
          this.innerMaterialElement.validationMessage,
        ),
        this.innerInputElement,
      );
    }
    this.validity = this.internals?.validity;

    // force a re-render by setting a new reference
    this.validationMessage = this.internals?.validationMessage;
    return this.validity?.valid;
  }

  async componentDidLoad() {
    if (this.alwaysShowValidationMessage) {
      this.touched = true;
    }
    this.innerMaterialElement = this.el.shadowRoot.getElementById('checkbox') as MdCheckbox;
    this.innerInputElement = (await awaitDefinedResult(() =>
      this.innerMaterialElement?.shadowRoot?.getElementById('input'),
    )) as HTMLInputElement;
    this.validateAfterNextRender = true;
  }

  componentDidRender() {
    if (this.validateAfterNextRender) {
      this.validateAfterNextRender = false;
      this.validate();
    }

    const slotted = this.el.shadowRoot.querySelector('omk-label slot') as HTMLSlotElement;
    this.labelPresent = slotted?.assignedElements().length > 0 || slotted?.assignedNodes().length > 0;
  }

  formAssociatedCallback(form) {
    selectFormSubmitters(form).forEach(submitter =>
      submitter.addEventListener('click', () => {
        this.touched = true;
        // re-validate just to be sure. libraryComponentOptions.componentValidationMessageOverrides might have changed.
        this.validateAfterNextRender = true;
      }),
    );
    if (form) {
      this.internals.setFormValue(this.checked ? this.value : null);
    }
  }
}
